import React, { useEffect, useMemo, useRef, useState } from "react";
import SequenceCanvas, { isStepEditable } from "./SequenceCanvas";
import StepPickerDrawer from "./StepPickerDrawer";
import SendMessageDrawer from "./SendMessageDrawer";
import VoiceNoteDrawer from "./VoiceNoteDrawer";
import LikePostDrawer from "./LikePostDrawer";
import ConnectionRequestDrawer from "./ConnectionRequestDrawer";
import TemplateBrowserModal from "./TemplateBrowserModal";
import {
  STEP_TYPES,
  createEmptySequence,
  createStepId,
  ensureSequenceShape,
  insertStepAtSlot,
  isTerminalStep,
  normalizeStepsDelays,
} from "./sequenceUtils";

export default function SequenceStep({ state, setState }) {
  const hasActions = (state.steps || []).some((s) => !isTerminalStep(s));
  const [mode, setMode] = useState(hasActions ? "canvas" : "chooser");
  // Slot the picker will insert into; null while the picker is closed.
  const [pickerSlot, setPickerSlot] = useState(null);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [editingStep, setEditingStep] = useState(null);

  // A saved sequence can arrive after mount; jump straight to the canvas once it does.
  const hadActions = useRef(hasActions);
  useEffect(() => {
    if (hasActions && !hadActions.current) setMode("canvas");
    hadActions.current = hasActions;
  }, [hasActions]);

  const steps = useMemo(
    () => (state.steps?.length ? ensureSequenceShape(state.steps) : createEmptySequence()),
    [state.steps],
  );

  const updateSteps = (next) => {
    setState((prev) => ({ ...prev, steps: next }));
  };

  const startFromScratch = () => {
    const fresh = createEmptySequence();
    updateSteps(fresh);
    setMode("canvas");
    setPickerSlot({ parentId: fresh[0].id, condition: null });
  };

  const handlePick = (item) => {
    if (!pickerSlot) return;
    const newStep = {
      id: createStepId(),
      type: item.type,
      label: item.label,
      content: "",
      delay: 0,
      delayUnit: "hours",
    };
    updateSteps(insertStepAtSlot(steps, pickerSlot, newStep));
    setPickerSlot(null);
    if (isStepEditable(newStep)) setEditingStep(newStep);
  };

  const handleImportTemplate = (template) => {
    const imported = normalizeStepsDelays(
      (template.steps || []).map((s, i) => ({
        ...s,
        id: s.id || createStepId(),
        order: s.order ?? i + 1,
      })),
    );
    const hasStart = imported.some((s) => s.type === "START");
    const hasEnd = imported.some((s) => s.type === STEP_TYPES.END);
    let next = imported;
    if (!hasStart) {
      const firstId = next[0]?.id;
      next = [
        {
          id: createStepId(),
          type: "START",
          label: "Sequence start",
          order: 0,
          nextStepId: firstId,
        },
        ...next,
      ];
    }
    if (!hasEnd) {
      next = [
        ...next,
        { id: createStepId(), type: STEP_TYPES.END, label: "Sequence end", order: next.length },
      ];
    }
    updateSteps(ensureSequenceShape(next));
    setTemplatesOpen(false);
    setMode("canvas");
  };

  const saveStep = (updated) => {
    updateSteps(steps.map((s) => (s.id === updated.id ? { ...s, ...updated } : s)));
    setEditingStep(null);
  };

  if (mode === "chooser") {
    return (
      <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 py-8">
        <div className="border border-gray-200 rounded-2xl p-6 bg-white shadow-sm flex flex-col">
          <div className="h-40 rounded-xl bg-gradient-to-br from-blue-50 to-slate-50 mb-4 flex items-center justify-center text-blue-400 text-sm">
            Blank canvas
          </div>
          <h3 className="text-lg font-semibold">Build it your way</h3>
          <p className="text-sm text-gray-500 mt-2 flex-1">
            Start from a blank canvas — full GTM control. Mix LinkedIn steps, conditions, and
            multichannel outreach your way.
          </p>
          <button
            type="button"
            onClick={startFromScratch}
            className="mt-4 w-full py-2.5 rounded-xl bg-blue-500 text-white text-sm font-medium"
          >
            Build from scratch
          </button>
        </div>
        <div className="border border-gray-200 rounded-2xl p-6 bg-white shadow-sm flex flex-col">
          <div className="h-40 rounded-xl bg-gradient-to-br from-emerald-50 to-slate-50 mb-4 flex items-center justify-center text-emerald-500 text-sm">
            Templates
          </div>
          <h3 className="text-lg font-semibold">Start with a template</h3>
          <p className="text-sm text-gray-500 mt-2 flex-1">
            Battle-tested outreach templates built for replies. Or bring your own — import from a
            file or an existing campaign.
          </p>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={() => setTemplatesOpen(true)}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-medium"
            >
              Browse
            </button>
            <button
              type="button"
              onClick={() => setTemplatesOpen(true)}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-medium"
            >
              Import
            </button>
          </div>
        </div>
        <TemplateBrowserModal
          open={templatesOpen}
          onClose={() => setTemplatesOpen(false)}
          onImport={handleImportTemplate}
        />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-280px)] min-h-[480px] flex flex-col border border-gray-200 rounded-2xl overflow-hidden bg-white">
      <div className="flex items-center justify-between px-4 py-2 border-b bg-white">
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500 hidden sm:inline">
            Use + to add a step anywhere, ✓ to end a path. Drag to pan, Ctrl + scroll to zoom.
          </span>
          <button
            type="button"
            onClick={() => setTemplatesOpen(true)}
            className="px-3 py-1.5 rounded-lg border text-sm"
          >
            Templates
          </button>
        </div>
        <button type="button" onClick={() => setMode("chooser")} className="text-sm text-gray-500">
          Change approach
        </button>
      </div>
      <div className="flex-1 min-h-0">
        <SequenceCanvas
          steps={steps}
          onChange={updateSteps}
          onAddStep={setPickerSlot}
          onEditStep={setEditingStep}
        />
      </div>
      <StepPickerDrawer
        open={Boolean(pickerSlot)}
        isFirstStep={!steps.some((s) => !isTerminalStep(s))}
        onClose={() => setPickerSlot(null)}
        onSelect={handlePick}
      />
      <SendMessageDrawer
        open={editingStep?.type === STEP_TYPES.MESSAGE || editingStep?.type === STEP_TYPES.IN_MAIL}
        step={editingStep}
        onClose={() => setEditingStep(null)}
        onSave={saveStep}
      />
      <VoiceNoteDrawer
        open={editingStep?.type === STEP_TYPES.VOICE_NOTE}
        step={editingStep}
        onClose={() => setEditingStep(null)}
        onSave={saveStep}
      />
      <LikePostDrawer
        open={editingStep?.type === STEP_TYPES.LIKE_POST}
        step={editingStep}
        onClose={() => setEditingStep(null)}
        onSave={saveStep}
      />
      <ConnectionRequestDrawer
        open={editingStep?.type === STEP_TYPES.CONNECTION_REQUEST}
        step={editingStep}
        onClose={() => setEditingStep(null)}
        onSave={saveStep}
      />
      <TemplateBrowserModal
        open={templatesOpen}
        onClose={() => setTemplatesOpen(false)}
        onImport={handleImportTemplate}
      />
    </div>
  );
}
